import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Printer,
  Clock,
  Settings2,
  QrCode,
  Settings,
  LogIn,
  LogOut,
  Menu,
  Headphones,
  Phone,
} from "lucide-react";

import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import CustomerSupportModal from "@/components/CustomerSupportModal";

const NAV = [
  {
    to: "/",
    label: "Scan to Print",
    icon: Printer,
    testid: "nav-scantoprint",
    end: true,
  },
  {
    to: "/jobs",
    label: "Print Jobs",
    icon: Clock,
    testid: "nav-jobs",
  },
  {
    to: "/printers",
    label: "Printer Setup",
    icon: Settings2,
    testid: "nav-printers",
  },
  {
    to: "/qr-setup",
    label: "Customer QR & Rates",
    icon: QrCode,
    testid: "nav-qr",
  },
  {
    to: "/settings",
    label: "Settings",
    icon: Settings,
    testid: "nav-settings",
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const [showSupportModal, setShowSupportModal] = useState(false);

  const [shop, setShop] = useState({
    shop_name_english: "",
    brand_name: "PrintQ",
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    api
      .get("/settings")
      .then((res) => {
        if (res.data) {
          setShop((curr) => ({
            ...curr,
            ...res.data,
          }));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div
      className="min-h-screen bg-zinc-950 text-white flex app-shell"
      style={{ "--app-header-height": "76px" }}
    >
      {/* --------------------------------------------------- */}
      {/* SIDEBAR                                             */}
      {/* --------------------------------------------------- */}
      <aside
        className={`
          app-sidebar
          fixed
          lg:sticky
          top-0
          left-0
          h-screen
          z-50
          transform
          transition-transform
          duration-250
          ease-out
          ${open ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
          flex
          flex-col
          bg-zinc-900/95
          border-r
          border-zinc-800/80
          w-64
          backdrop-blur-md
        `}
        data-testid="sidebar"
      >
        {/* Brand Block */}
        <div className="p-5 border-b border-zinc-800/80">
          <Logo size="md" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all duration-200 ${
                    isActive
                      ? "bg-amber-500 text-zinc-950 shadow-md shadow-amber-500/20 font-bold scale-[1.02]"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-800/60 hover:translate-x-1"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={2} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* User / Logout */}
        <div className="p-4 border-t border-zinc-800/80">
          {user ? (
            <div className="space-y-2.5">
              <div className="text-xs text-zinc-400 truncate flex items-center gap-2" data-testid="sidebar-user-email">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="truncate font-medium">{user.username || user.name || "Operator"}</span>
              </div>

              <button
                className="w-full py-2 px-3 rounded-xl bg-zinc-800/80 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold flex items-center justify-center gap-2 transition-all duration-150 border border-zinc-700/60 active:scale-95"
                onClick={() => {
                  logout();
                  nav("/login");
                }}
                data-testid="logout-btn"
              >
                <LogOut className="w-3.5 h-3.5" />
                Logout
              </button>
            </div>
          ) : (
            <button
              className="w-full py-2.5 px-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-xs flex items-center justify-center gap-2 shadow-md shadow-amber-500/20 transition-all duration-150 active:scale-95"
              onClick={() => nav("/login")}
              data-testid="admin-login-cta"
            >
              <LogIn className="w-4 h-4" />
              Login
            </button>
          )}

          {/* Customer Support Button */}
          <button
            onClick={() => setShowSupportModal(true)}
            className="w-full mt-2.5 py-2 px-3 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-semibold flex items-center justify-center gap-2 transition-all duration-150 active:scale-95"
            data-testid="customer-support-sidebar-btn"
          >
            <Headphones className="w-3.5 h-3.5 text-amber-400" />
            <span>Support (9545262332)</span>
          </button>

          <div className="text-[9px] font-semibold tracking-wider text-zinc-400 text-center uppercase mt-3">
            PrintQ • JSM Digital Solutions
          </div>
        </div>
      </aside>

      {/* Mobile Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/70 z-40 lg:hidden backdrop-blur-sm transition-opacity duration-200"
          onClick={() => setOpen(false)}
        />
      )}

      {/* --------------------------------------------------- */}
      {/* MAIN CONTENT COLUMN                                 */}
      {/* --------------------------------------------------- */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <header className="h-[76px] bg-zinc-900/60 border-b border-zinc-800/80 backdrop-blur-md sticky top-0 z-30 flex items-center px-4 sm:px-6">
          <div className="flex items-center justify-between w-full">
            <button
              className="lg:hidden text-zinc-300 hover:text-white p-2"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="hidden sm:block">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-black tracking-tight text-white">Print</span>
                <span className="text-xs font-black tracking-tight text-amber-400">Q</span>
                <span className="text-[10px] text-zinc-400 mx-1.5">•</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-semibold">
                  Scan. Enhance. Print.
                </span>
              </div>
              <div className="text-xs text-zinc-400 mt-0.5">
                {shop.shop_name_english || user?.shop_name || "Digital Service / Xerox Center"}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Customer Support Direct Trigger */}
              <button
                onClick={() => setShowSupportModal(true)}
                className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-semibold transition-all active:scale-95"
              >
                <Headphones className="w-3.5 h-3.5 text-amber-400" />
                <span>Support</span>
              </button>

              {/* Live Clock */}
              <div
                className="flex items-center gap-2 text-xs text-zinc-300 bg-zinc-950/60 px-3.5 py-1.5 rounded-xl border border-zinc-800/80 shadow-sm"
                data-testid="live-clock"
              >
                <Clock className="w-3.5 h-3.5 text-amber-400" />
                <span className="font-mono">
                  {now.toLocaleDateString("en-IN", {
                    weekday: "short",
                    day: "2-digit",
                    month: "short",
                  })}
                  {" • "}
                  {now.toLocaleTimeString("en-IN", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Outlet */}
        <main className="flex-1 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      <CustomerSupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />
    </div>
  );
}